import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  UserPlus,
  Search,
  RefreshCw,
  Mail,
  Phone,
  Shield,
  Edit3,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Sparkles,
} from "lucide-react";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import ApiClient from "../../../methods/api/apiClient";
import loader from "../../../methods/loader";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { STATUS_OPTIONS, getRoleMeta, getStatusMeta, formatPhone } from "./shared";

export default function TeamList() {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [quotas, setQuotas] = useState({ current: 0, max: 5 });

  // Client & server filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch Customers / Users List (Always send status="all" to backend to prevent 500 errors)
  const fetchTeamMembers = useCallback(async () => {
    setLoading(true);
    loader(true);

    try {
      const params = {
        page: 1,
        limit: 100,
        status: "all",
        search: debouncedSearch.trim(),
      };

      const res = await ApiClient.get("tenants/portal/team", params, "", true);

      if (res?.success || Array.isArray(res?.data) || Array.isArray(res?.team) || res?.members) {
        const list = res.data?.members || res.data?.team || res.data || res.members || res.team || [];
        setTeamMembers(Array.isArray(list) ? list : []);

        const total = res.total || res.data?.total || res.data?.count || (Array.isArray(list) ? list.length : 0);
        setTotalCount(Number(total) || 0);

        if (res.quota || res.data?.quota || res.quotas) {
          const q = res.quota || res.data?.quota || res.quotas;
          setQuotas({
            current: q.current || q.team_members || q.used || list.length,
            max: q.max || q.max_users || q.limit || 5,
          });
        } else {
          setQuotas((prev) => ({
            ...prev,
            current: Array.isArray(list) ? list.length : prev.current,
          }));
        }
      } else {
        setTeamMembers([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.warn("Notice loading customers:", err);
    } finally {
      loader(false);
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  // Filter members client-side to prevent backend 500 errors on status queries
  const displayedMembers = useMemo(() => {
    let result = teamMembers;

    if (status !== "all") {
      result = result.filter((m) => {
        const s = (m.status || "active").toLowerCase();
        if (status === "active") return s === "active" || s === "enabled";
        if (status === "deactive") return s === "deactive" || s === "disabled" || s === "inactive";
        return true;
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter((m) => {
        const name = `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase();
        const email = (m.email || "").toLowerCase();
        const phone = (m.mobileNo || m.phone || "").toLowerCase();
        return name.includes(q) || email.includes(q) || phone.includes(q);
      });
    }

    return result;
  }, [teamMembers, status, search]);

  // Paginated slice
  const paginatedMembers = useMemo(() => {
    const start = (page - 1) * limit;
    return displayedMembers.slice(start, start + limit);
  }, [displayedMembers, page, limit]);

  // Toggle Active / Deactive Status
  const handleToggleStatus = async (member) => {
    const memberId = member._id || member.id;
    const currentStatus = (member.status || "active").toLowerCase();
    const newStatus = currentStatus === "active" ? "deactive" : "active";

    try {
      loader(true);
      const res = await ApiClient.post(
        "tenants/portal/team/status",
        { id: memberId, status: newStatus },
        "",
        true
      );

      if (res?.success || !res?.error) {
        toast.success(`Customer status changed to ${newStatus}`);
        setTeamMembers((prev) =>
          prev.map((m) =>
            (m._id || m.id) === memberId ? { ...m, status: newStatus } : m
          )
        );
      } else {
        toast.error(res?.message || "Failed to update status");
      }
    } catch (err) {
      toast.error("Error updating status");
    } finally {
      loader(false);
    }
  };

  // Delete Customer
  const handleDeleteMember = async (member) => {
    const memberId = member._id || member.id;
    const fullName = `${member.firstName || ""} ${member.lastName || ""}`.trim() || "this customer";

    const confirmed = await swal({
      title: "Remove Customer?",
      text: `Are you sure you want to remove ${fullName}? Their access will be revoked immediately.`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Remove"],
      dangerMode: true,
    });

    if (confirmed) {
      loader(true);
      try {
        const res = await ApiClient.delete("tenants/portal/team/delete", { id: memberId }, "", true);
        if (res?.success || !res?.error) {
          toast.success("Customer removed successfully");
          fetchTeamMembers();
        } else {
          toast.error(res?.message || "Failed to remove customer");
        }
      } catch (err) {
        toast.error("Error removing customer");
      } finally {
        loader(false);
      }
    }
  };

  const totalPages = Math.ceil(displayedMembers.length / limit) || 1;
  const isQuotaFull = quotas.current >= quotas.max;

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F7FB] antialiased text-slate-800">
      <Header />

      {/* Container matching Dashboard width: max-w-5xl */}
      <div className="min-h-[calc(100vh_-_60px)] md:min-h-[calc(100vh_-_70px)] mt-[60px] lg:mt-[70px] py-6 lg:py-8">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8 max-w-5xl space-y-5 pb-32">
          {/* ─── SLEEK MODERN PAGE HEADER (AIRY, NO BULKY CARD) ─── */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-1">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                Customers
              </h1>

              <p className="text-xs sm:text-sm text-slate-600 mt-1">
                Manage your customer portal accounts, view quoting permissions, and seat allocations.
              </p>
            </div>

            {/* Right Side Quota Pill & Primary CTA Button */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2.5 bg-white border border-slate-200/90 rounded-xl px-3.5 py-2.5 shadow-xs text-xs">
                <Users size={15} className="text-[#115B9D]" />
                <span className="text-slate-600 font-medium">
                  Seats: <strong className="font-bold text-slate-900">{quotas.current} / {quotas.max}</strong>
                </span>
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                {isQuotaFull ? (
                  <Link
                    to="/tenant/plans"
                    className="font-bold text-amber-700 hover:underline"
                  >
                    Limit Reached
                  </Link>
                ) : (
                  <Link
                    to="/tenant/plans"
                    className="font-bold text-[#115B9D] hover:text-[#0e4b82] hover:underline"
                  >
                    Upgrade
                  </Link>
                )}
              </div>

              <Link
                to="/tenant/customers/add"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#115B9D] to-[#1E88E5] text-white text-xs sm:text-sm font-bold shadow-md shadow-[#115B9D]/20 hover:shadow-lg transition-all active:scale-95 cursor-pointer whitespace-nowrap"
              >
                <UserPlus size={15} />
                <span>Invite Customer</span>
              </Link>
            </div>
          </div>

          {/* ─── MAIN CONTENT CARD (SEARCH, FILTERS, TABLE) ─── */}
          <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-300 shadow-sm overflow-hidden">
            {/* Integrated Search & Filter Header */}
            <div className="p-4 sm:p-5 border-b border-slate-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-slate-50/50">
              {/* Search Box */}
              <div className="relative flex-1 max-w-sm">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search customers by name, email, or phone..."
                  className="w-full pl-9 pr-8 py-2 rounded-xl border border-slate-200 text-xs bg-white focus:outline-none focus:border-[#115B9D] focus:ring-1 focus:ring-[#115B9D]/20 transition-all placeholder:text-slate-400"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Status Filters & Refresh */}
              <div className="flex items-center gap-2 self-start sm:self-auto">
                <div className="flex items-center gap-1 p-1 bg-white border border-slate-200 rounded-xl">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setStatus(opt.id);
                        setPage(1);
                      }}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        status === opt.id
                          ? "bg-[#115B9D] text-white shadow-2xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={fetchTeamMembers}
                  disabled={loading}
                  className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
                  title="Refresh"
                >
                  <RefreshCw size={14} className={loading ? "animate-spin text-[#115B9D]" : ""} />
                </button>
              </div>
            </div>

            {/* Content Body */}
            {loading && teamMembers.length === 0 ? (
              <div className="p-12 text-center">
                <RefreshCw size={24} className="animate-spin text-[#115B9D] mx-auto mb-3" />
                <p className="text-xs font-bold text-slate-700">Loading customers...</p>
              </div>
            ) : displayedMembers.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 text-[#115B9D] flex items-center justify-center mx-auto mb-3">
                  <Users size={22} />
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1">No Customers Found</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto mb-4">
                  {search || status !== "all"
                    ? "No customers match your active filter criteria. Clear filters to see all."
                    : "Invite customers to access your quoting portal and view equipment catalogs."}
                </p>
                <Link
                  to="/tenant/customers/add"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#115B9D] text-white text-xs font-bold hover:bg-[#0e4b82] transition-colors"
                >
                  <UserPlus size={14} />
                  <span>Invite First Customer</span>
                </Link>
              </div>
            ) : (
              <>
                {/* DESKTOP RESPONSIVE TABLE (NO HORIZONTAL SCROLL) */}
                <div className="hidden md:block overflow-x-hidden">
                  <table className="w-full text-left border-collapse table-fixed">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/60 text-[11px] font-black uppercase tracking-wider text-slate-500">
                        <th className="py-3 px-5 w-[38%]">Customer</th>
                        <th className="py-3 px-4 w-[24%]">Contact Phone</th>
                        <th className="py-3 px-4 w-[18%]">Status</th>
                        <th className="py-3 px-5 w-[20%] text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                      {paginatedMembers.map((member) => {
                        const memberId = member._id || member.id;
                        const fullName = `${member.firstName || ""} ${member.lastName || ""}`.trim() || "Customer";
                        const statusMeta = getStatusMeta(member.status);
                        const initials = ((member.firstName?.[0] || "") + (member.lastName?.[0] || "")).toUpperCase() || "CU";

                        return (
                          <tr key={memberId} className="hover:bg-blue-50/20 transition-colors group">
                            {/* Member & Email */}
                            <td className="py-3.5 px-5">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-[#115B9D]/10 text-[#115B9D] font-black text-xs flex items-center justify-center shrink-0">
                                  {initials}
                                </div>
                                <div className="truncate">
                                  <Link
                                    to={`/tenant/customers/detail/${memberId}`}
                                    className="font-bold text-slate-900 hover:text-[#115B9D] transition-colors truncate block"
                                  >
                                    {fullName}
                                  </Link>
                                  <span className="text-[11px] text-slate-400 flex items-center gap-1 truncate">
                                    <Mail size={10} className="text-slate-400 shrink-0" />
                                    <span className="truncate">{member.email}</span>
                                  </span>
                                </div>
                              </div>
                            </td>

                            {/* Phone */}
                            <td className="py-3.5 px-4 text-slate-600">
                              <span className="flex items-center gap-1.5">
                                <Phone size={11} className="text-slate-400" />
                                <span>{formatPhone(member.mobileNo || member.phone)}</span>
                              </span>
                            </td>

                            {/* Status Button */}
                            <td className="py-3.5 px-4">
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(member)}
                                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border transition-all cursor-pointer ${statusMeta.badgeClass}`}
                                title="Click to toggle status"
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dotClass}`} />
                                <span>{statusMeta.label}</span>
                              </button>
                            </td>

                            {/* Actions */}
                            <td className="py-3.5 px-5 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Link
                                  to={`/tenant/customers/detail/${memberId}`}
                                  className="p-1 rounded-lg text-slate-400 hover:text-[#115B9D] hover:bg-blue-50 transition-colors"
                                  title="View"
                                >
                                  <Eye size={14} />
                                </Link>
                                <Link
                                  to={`/tenant/customers/edit/${memberId}`}
                                  className="p-1 rounded-lg text-slate-400 hover:text-[#115B9D] hover:bg-blue-50 transition-colors"
                                  title="Edit"
                                >
                                  <Edit3 size={14} />
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteMember(member)}
                                  className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* MOBILE CARD LIST (NO HORIZONTAL SCROLL) */}
                <div className="md:hidden divide-y divide-slate-100 p-3 space-y-3">
                  {paginatedMembers.map((member) => {
                    const memberId = member._id || member.id;
                    const fullName = `${member.firstName || ""} ${member.lastName || ""}`.trim() || "Customer";
                    const statusMeta = getStatusMeta(member.status);
                    const initials = ((member.firstName?.[0] || "") + (member.lastName?.[0] || "")).toUpperCase() || "CU";

                    return (
                      <div key={memberId} className="p-3 bg-white rounded-2xl border border-slate-200/80 space-y-2.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-[#115B9D]/10 text-[#115B9D] font-bold text-xs flex items-center justify-center shrink-0">
                              {initials}
                            </div>
                            <div className="truncate">
                              <Link to={`/tenant/customers/detail/${memberId}`} className="font-bold text-slate-900 text-xs block truncate">
                                {fullName}
                              </Link>
                              <span className="text-[11px] text-slate-400 truncate block">{member.email}</span>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleToggleStatus(member)}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusMeta.badgeClass}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dotClass}`} />
                            <span>{statusMeta.label}</span>
                          </button>
                        </div>

                        <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1.5 border-t border-slate-100">
                          <span className="font-medium text-slate-600">Contact</span>
                          <span>{formatPhone(member.mobileNo || member.phone)}</span>
                        </div>

                        <div className="grid grid-cols-3 gap-1.5 pt-1 border-t border-slate-100 text-center text-xs">
                          <Link
                            to={`/tenant/customers/detail/${memberId}`}
                            className="py-1 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50"
                          >
                            View
                          </Link>
                          <Link
                            to={`/tenant/customers/edit/${memberId}`}
                            className="py-1 rounded-lg border border-slate-200 text-[#115B9D] font-semibold hover:bg-blue-50"
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDeleteMember(member)}
                            className="py-1 rounded-lg border border-rose-200 text-rose-600 font-semibold hover:bg-rose-50 cursor-pointer"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* PAGINATION */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs bg-slate-50/40">
                    <span className="text-slate-500">
                      Showing <strong>{(page - 1) * limit + 1}</strong> to{" "}
                      <strong>{Math.min(page * limit, displayedMembers.length)}</strong> of{" "}
                      <strong>{displayedMembers.length}</strong>
                    </span>

                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className="px-3 py-1 rounded-lg border border-slate-200 bg-white text-slate-700 font-bold hover:bg-slate-50 disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <span className="px-2 font-bold text-slate-800">
                        {page} / {totalPages}
                      </span>
                      <button
                        type="button"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        className="px-3 py-1 rounded-lg border border-slate-200 bg-white text-slate-700 font-bold hover:bg-slate-50 disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
